if (!customElements.get('collection-filters')) {
  class CollectionFilters extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.requestId = 0;
      this.listTimer = null;

      this.onClick = this.onClick.bind(this);
      this.onChange = this.onChange.bind(this);
      this.onInput = this.onInput.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onDocumentClick = this.onDocumentClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onScroll = this.onScroll.bind(this);
      this.scrollFrame = null;

      this.addEventListener('click', this.onClick);
      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('keydown', this.onKeydown);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      document.addEventListener('click', this.onDocumentClick);
      window.addEventListener('popstate', this.onPopState);
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.initPriceSliders();
      this.updateStuck();
      this.observeInfinite();
    }

    disconnectedCallback() {
      document.removeEventListener('click', this.onDocumentClick);
      window.removeEventListener('popstate', this.onPopState);
      window.removeEventListener('scroll', this.onScroll);
      if (this.infiniteObserver) this.infiniteObserver.disconnect();
    }

    /* ---------- sticky bar ---------- */

    // Keeps tracking for a moment after scrolling stops so the bar follows the header's slide animation.
    onScroll() {
      this.settleUntil = performance.now() + 400;
      if (this.scrollFrame) return;
      const tick = () => {
        this.updateStuck();
        this.scrollFrame = performance.now() < this.settleUntil ? requestAnimationFrame(tick) : null;
      };
      this.scrollFrame = requestAnimationFrame(tick);
    }

    // The lowest visible edge of anything pinned above the bar: the sticky announcement strip and/or the header.
    headerOffset() {
      let offset = 0;
      const announcement = document.querySelector('[data-cbm-ann-sticky]');
      if (announcement) offset = Math.max(offset, Math.round(announcement.getBoundingClientRect().bottom));

      const header = document.querySelector('.section-header .header-wrapper') || document.querySelector('.section-header');
      if (header) {
        const position = getComputedStyle(header.closest('.section-header') || header).position;
        if (position === 'sticky' || position === 'fixed') {
          offset = Math.max(offset, Math.round(header.getBoundingClientRect().bottom));
        }
      }
      return Math.max(0, offset);
    }

    updateStuck() {
      const bar = this.querySelector('.cf__bar--sticky');
      if (!bar) return;
      const offset = this.headerOffset();
      if (offset !== this.lastOffset) {
        this.lastOffset = offset;
        bar.style.setProperty('--cf-sticky-top', `${offset}px`);
      }
      const stuck = bar.getBoundingClientRect().top <= offset + 1 && window.scrollY > 0;
      bar.classList.toggle('is-stuck', stuck);
    }

    /* ---------- events ---------- */

    onClick(event) {
      const target = event.target;

      const ddTrigger = target.closest('[data-cf-dd-trigger]');
      if (ddTrigger) {
        const dd = ddTrigger.closest('[data-cf-dd]');
        dd.classList.contains('is-open') ? this.closeDropdown(dd) : this.openDropdown(dd);
        return;
      }

      const priceApply = target.closest('[data-cf-price-apply]');
      if (priceApply) {
        const params = this.currentParams();
        this.writePrice(params, priceApply.closest('[data-cf-price]'));
        this.render(params);
        return;
      }

      const priceReset = target.closest('[data-cf-price-reset]');
      if (priceReset) {
        const box = priceReset.closest('[data-cf-price]');
        const params = this.currentParams();
        params.delete(box.dataset.minName);
        params.delete(box.dataset.maxName);
        this.render(params);
        return;
      }

      const more = target.closest('[data-cf-more]');
      if (more) {
        event.preventDefault();
        this.loadMore(more);
        return;
      }

      const link = target.closest('a[data-cf-link], .pagination a');
      if (link && link.href) {
        event.preventDefault();
        const isPagination = Boolean(link.closest('.pagination'));
        const params = new URL(link.href, window.location.origin).searchParams;
        this.render(params, { scroll: isPagination });
      }
    }

    onChange(event) {
      const input = event.target;

      if (input.closest('[data-cf-sort]')) {
        const params = this.currentParams();
        params.set('sort_by', input.value);
        this.closeDropdown(input.closest('[data-cf-dd]'));
        this.render(params);
        return;
      }

      const list = input.closest('[data-cf-list]');
      if (list && input.closest('[data-cf-dd]')) {
        clearTimeout(this.listTimer);
        this.listTimer = setTimeout(() => this.applyList(list, input), 250);
        return;
      }

      if (input.matches('[data-cf-input]')) this.syncFromInput(input);
    }

    onInput(event) {
      if (event.target.matches('[data-cf-range]')) this.syncFromRange(event.target);
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        const openDd = this.querySelector('[data-cf-dd].is-open');
        if (openDd) {
          this.closeDropdown(openDd);
          openDd.querySelector('[data-cf-dd-trigger]').focus();
        }
        return;
      }

      if (event.key === 'Enter' && event.target.matches('[data-cf-dd] [data-cf-input]')) {
        event.preventDefault();
        const box = event.target.closest('[data-cf-price]');
        this.syncFromInput(event.target);
        const params = this.currentParams();
        this.writePrice(params, box);
        this.render(params);
      }
    }

    onDocumentClick(event) {
      if (event.target.closest('[data-cf-dd]')) return;
      this.querySelectorAll('[data-cf-dd].is-open').forEach((dd) => this.closeDropdown(dd));
    }

    onPopState() {
      this.render(this.currentParams(), { push: false });
    }

    /* ---------- dropdowns ---------- */

    openDropdown(dd, instant = false) {
      this.querySelectorAll('[data-cf-dd].is-open').forEach((other) => {
        if (other !== dd) this.closeDropdown(other);
      });
      if (instant) dd.classList.add('is-instant');
      dd.classList.add('is-open');
      dd.querySelector('[data-cf-dd-trigger]').setAttribute('aria-expanded', 'true');

      const panel = dd.querySelector('[data-cf-dd-panel]');
      panel.classList.remove('is-flipped');
      const rect = panel.getBoundingClientRect();
      if (rect.right > window.innerWidth - 12) panel.classList.add('is-flipped');

      if (instant) requestAnimationFrame(() => requestAnimationFrame(() => dd.classList.remove('is-instant')));
    }

    closeDropdown(dd) {
      if (!dd) return;
      dd.classList.remove('is-open');
      dd.querySelector('[data-cf-dd-trigger]').setAttribute('aria-expanded', 'false');
    }

    /* ---------- list filters ---------- */

    applyList(list, changedInput) {
      const params = this.currentParams();
      const inputs = list.querySelectorAll('input[type="checkbox"]');
      new Set(Array.from(inputs, (input) => input.name)).forEach((name) => params.delete(name));
      inputs.forEach((input) => {
        if (input.checked) params.append(input.name, input.value);
      });
      const dd = list.closest('[data-cf-dd]');
      this.render(params, { keepOpen: dd ? dd.id : null, focusId: changedInput ? changedInput.id : null });
    }

    /* ---------- price ---------- */

    initPriceSliders() {
      this.querySelectorAll('[data-cf-price]').forEach((box) => this.paintSlider(box));
    }

    priceParts(box) {
      return {
        rangeMin: box.querySelector('[data-cf-range="min"]'),
        rangeMax: box.querySelector('[data-cf-range="max"]'),
        inputMin: box.querySelector('[data-cf-input="min"]'),
        inputMax: box.querySelector('[data-cf-input="max"]'),
        max: Number(box.dataset.rangeMax) || 0,
      };
    }

    syncFromRange(range) {
      const box = range.closest('[data-cf-price]');
      const p = this.priceParts(box);
      let lo = Number(p.rangeMin.value);
      let hi = Number(p.rangeMax.value);
      if (lo > hi) {
        if (range === p.rangeMin) {
          lo = hi;
          p.rangeMin.value = lo;
        } else {
          hi = lo;
          p.rangeMax.value = hi;
        }
      }
      p.inputMin.value = lo > 0 ? lo : '';
      p.inputMax.value = hi < p.max ? hi : '';
      this.paintSlider(box);
    }

    syncFromInput(input) {
      const box = input.closest('[data-cf-price]');
      const p = this.priceParts(box);
      const clamp = (n) => Math.min(Math.max(n, 0), p.max);
      let lo = p.inputMin.value === '' ? 0 : clamp(Number(p.inputMin.value));
      let hi = p.inputMax.value === '' ? p.max : clamp(Number(p.inputMax.value));
      if (lo > hi) [lo, hi] = [hi, lo];
      p.inputMin.value = lo > 0 ? lo : '';
      p.inputMax.value = hi < p.max ? hi : '';
      p.rangeMin.value = lo;
      p.rangeMax.value = hi;
      this.paintSlider(box);
    }

    paintSlider(box) {
      const p = this.priceParts(box);
      if (!p.rangeMin || !p.max) return;
      box.style.setProperty('--cf-from', `${(Number(p.rangeMin.value) / p.max) * 100}%`);
      box.style.setProperty('--cf-to', `${(Number(p.rangeMax.value) / p.max) * 100}%`);
    }

    writePrice(params, box) {
      const p = this.priceParts(box);
      params.delete(box.dataset.minName);
      params.delete(box.dataset.maxName);
      const lo = Number(p.inputMin.value);
      const hi = Number(p.inputMax.value);
      if (p.inputMin.value !== '' && lo > 0) params.set(box.dataset.minName, lo);
      if (p.inputMax.value !== '' && hi < p.max) params.set(box.dataset.maxName, hi);
    }

    /* ---------- rendering ---------- */

    /* ---------- load more ---------- */

    async loadMore(button) {
      if (this.loadingMore || !button.href) return;
      this.loadingMore = true;
      button.classList.add('is-loading');
      button.setAttribute('aria-busy', 'true');

      const url = new URL(button.href, window.location.origin);
      url.searchParams.set('section_id', this.sectionId);

      try {
        const response = await fetch(url.toString());
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const grid = this.querySelector('#product-grid');
        const items = doc.querySelectorAll('#product-grid > li');
        const firstNew = items[0];

        items.forEach((item) => {
          item.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));
          item.classList.add('scroll-trigger--cancel');
          grid.appendChild(item);
        });

        const freshMore = doc.querySelector('[data-cf-more-wrap]');
        const currentMore = this.querySelector('[data-cf-more-wrap]');
        if (currentMore) {
          if (freshMore) currentMore.replaceWith(freshMore);
          else currentMore.remove();
        }

        if (firstNew && button === document.activeElement) {
          firstNew.querySelector('a[href]')?.focus({ preventScroll: true });
        }
        this.observeInfinite();
        this.dispatchEvent(new CustomEvent('collection:updated', { bubbles: true, detail: { url: url.toString() } }));
      } catch (error) {
        console.error(error);
        window.location.href = button.href;
      } finally {
        this.loadingMore = false;
      }
    }

    observeInfinite() {
      if (this.infiniteObserver) this.infiniteObserver.disconnect();
      const trigger = this.querySelector('[data-cf-more][data-cf-infinite]');
      if (!trigger || !('IntersectionObserver' in window)) return;
      this.infiniteObserver = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) this.loadMore(trigger);
        },
        { rootMargin: '600px 0px' }
      );
      this.infiniteObserver.observe(trigger);
    }

    currentParams() {
      const params = new URLSearchParams(window.location.search);
      params.delete('page');
      params.delete('section_id');
      return params;
    }

    async render(params, { keepOpen = null, focusId = null, push = true, scroll = false } = {}) {
      params.delete('section_id');
      const query = params.toString();
      const pageUrl = `${window.location.pathname}${query ? `?${query}` : ''}`;
      const fetchUrl = `${window.location.pathname}?section_id=${this.sectionId}${query ? `&${query}` : ''}`;
      const requestId = ++this.requestId;

      this.classList.add('is-loading');

      try {
        let html = this.cache.get(fetchUrl);
        if (!html) {
          const response = await fetch(fetchUrl);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          html = await response.text();
          this.cache.set(fetchUrl, html);
        }
        if (requestId !== this.requestId) return;

        const fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('collection-filters');
        if (!fresh) throw new Error('collection-filters missing from section response');

        this.innerHTML = fresh.innerHTML;
        this.querySelectorAll('.scroll-trigger').forEach((el) => el.classList.add('scroll-trigger--cancel'));
        this.initPriceSliders();
        this.lastOffset = null;
        this.updateStuck();
        this.observeInfinite();

        if (keepOpen) {
          const dd = document.getElementById(keepOpen);
          if (dd) this.openDropdown(dd, true);
        }
        if (focusId) document.getElementById(focusId)?.focus({ preventScroll: true });
        if (push) history.pushState({ collectionFilters: true }, '', pageUrl);
        if (scroll) this.scrollIntoView({ behavior: 'smooth', block: 'start' });

        this.dispatchEvent(new CustomEvent('collection:updated', { bubbles: true, detail: { url: pageUrl } }));
      } catch (error) {
        console.error(error);
        window.location.href = pageUrl;
      } finally {
        if (requestId === this.requestId) this.classList.remove('is-loading');
      }
    }
  }

  customElements.define('collection-filters', CollectionFilters);
}
